import React, { useState, useCallback, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import authHeader from "../../services/auth-header";
import _db from "../../services/kiosque-service";
import useInactivityMonitor from './hooks/useInactivityMonitor';
import Header from './Header';
import StepOne from './StepOne';

import './styles.css';

// Importations dynamiques (Lazy Loading)
const StepTwo = lazy(() => import('./StepTwo'));
const StepThree = lazy(() => import('./StepThree'));
const StepFour = lazy(() => import('./StepFour'));
const StepFive = lazy(() => import('./StepFive'));
const StepSix = lazy(() => import('./StepSix'));
const StepSeven = lazy(() => import('./StepSeven'));
const StepEight = lazy(() => import('./StepEight'));

const INITIAL_FORM_DATA = {
    sessionId: crypto.randomUUID(),
    truckingCompany: '',
    truckNumber: '',
    licencePlate: '',
    bookNumber: '',
    bookData: {},
    bookedEquipment: [],
    isBundle: false,
    terminal: {},
    transport: {},
    errorMessage: '',
    attachGenset: false,
    interchangeNumber: [],
    user: '',
    line: {},
    genset: {},
    qtyToPickUp: '',
};

const LoadingSpinner = () => {
    return (
        <div className="text-center kform">
            <i className="fa fa-spinner fa-spin d-block fs-2 p-2"></i>
        </div>
    );
};

const Elift = () => {
    const { i18n } = useTranslation();

    const [formData, setFormData] = useState(INITIAL_FORM_DATA);
    const [stepHistory, setStepHistory] = useState([1]);  // Tableau pour stocker les étapes visitées, commençant par l'étape 1
    const currentStep = stepHistory[stepHistory.length - 1]; // L'étape courrante est toujours la dernière du tableau d'historique  

    const updateFormData = (data) => {
        setFormData(prevData => ({ ...prevData, ...data }));
    };

    const cardStyle = {
        boxShadow: '0px 0px 30px 0px rgba(0, 0, 0, 0.3)',
        maxWidth: '600px'
    };

    /**
     * Gère la navigation vers une nouvelle étape (forward, >= 1) ou le retour en arrière (-1).
     * @param {number} destination - Le numéro de l'étape cible ou -1 pour revenir en arrière.
     */
    const handleNavigation = useCallback((destination) => {

        setStepHistory(prevHistory => {

            // --- CAS 1 : Gérer la navigation vers une étape spécifique (FORWARD) ---
            // On vérifie si la destination est un step valide (entre 1 et 8)
            if (destination >= 1 && destination <= 8) {
                // Ajoute la nouvelle étape à l'historique
                return [...prevHistory, destination];
            }

            // --- CAS 2 : Gérer le retour en arrière (BACKWARD) ---
            else if (destination === -1) {
                if (prevHistory.length > 1) {
                    // Supprime la dernière étape de l'historique
                    const newHistory = prevHistory.slice(0, prevHistory.length - 1); // Créer un nouvel historique sans la dernière étape (l'étape actuelle)
                    return newHistory;
                    //return prevHistory.slice(0, prevHistory.length - 1);
                }
                return prevHistory; // Reste sur la première étape si on ne peut pas revenir plus loin
            }

            // --- CAS 3 : Gérer les entrées invalides ---
            else {
                console.error(`Tentative de navigation vers une destination invalide : ${destination}`);
                return prevHistory; // Ne change pas l'historique
            }
        });

    }, []); // Dépendances vides

    const handleReset = () => {
        // 1. On génère un nouvel ID (qui nettoie les composants via la 'key')
        const newSessionId = crypto.randomUUID();

        // 2. On met à jour le formulaire global
        setFormData(prev => ({
            ...INITIAL_FORM_DATA,
            sessionId: newSessionId,    // Nouvelle session pour le camionneur suivant
            user: prev.user,            // Préserve la peronne connectée
            terminal: prev.terminal     // Préserve le terminal
        }));

        // 3. On revient au début
        setStepHistory([1]);
    };

    const logAction = useCallback(async (step, action, details = {}, isSuccess = null) => {

        // Calcul de la chaîne d'équipement : Taille + Type + Hauteur
        const getEquipDesc = (item) => {
            if (!item?.size) return null;

            // Règle spécifique : si size est "GENSET", on ignore le reste
            if (item.size === "GENSET") {
                return "GENSET";
            }

            // Sinon, format complet : Size Type Height
            const sizeType = `${item.size} ${item.type || ''}`.trim();
            const height = item.height ? ` ${item.height}` : '';
            return (sizeType + height).trim();
        };

        const equipment = getEquipDesc(formData.line);
        //const equipment = getEquipDesc(formData.line) || getEquipDesc(formData.genset);

        const logEntry = {
            sessionId: formData.sessionId,
            bookNum: formData.bookData?.bookNum || null,
            stepNumber: step,
            actionType: action,
            languageCode: i18n.language,
            userName: formData.user || null,
            terminal: formData.terminal?.terminalCode || null,
            truckNum: formData.truckNumber || null,
            licencePlate: formData.licencePlate || null,
            truckingCompany: formData.transport?.transport1 || null,
            equipmentType: equipment,
            isSuccess: isSuccess,
            details: JSON.stringify(details)
        };

        try {
            await _db.post("/api/kiosquelogaction", logEntry, { headers: authHeader() });
        } catch (error) {
            console.error("Log Error:", error.message);
        }
    }, [formData.sessionId, formData.bookNumber, formData.bookData?.bookNum, formData.user, formData.truckNumber,
    formData.licencePlate, formData.truckingCompany, formData.line, formData.genset, i18n.language]);

    //const logAction = useCallback(async (step, action, details = {}) => {
    //    const logEntry = {
    //        sessionId: formData.sessionId, 
    //        bookNum: formData.bookData?.bookNum,
    //        stepNumber: step,
    //        actionType: action,
    //        languageCode: i18n.language,
    //        details: JSON.stringify(details)
    //    };

    //    try {
    //        await _db.post("/api/kiosquelogaction", logEntry, { headers: authHeader() });
    //        //await _db.post("/api/kiosqueLogAction", logEntry, { headers: {...authHeader(),n"Content-Type": "application/json" } });
    //    } catch (error) {
    //        console.error("Erreur de log :", error.response?.data || error.message);
    //    }
    //}, [formData.sessionId, formData.bookData?.bookNum, i18n.language]);

    // Fonction qui sera appelée après 30 secondes d'inactivité
    const handleInactivityTimeout = useCallback(() => {

        // CONDITION : Activity si on a dépassé l'étape 1 OU si un champ est rempli
        const hasInput = Object.values(formData).some(val => val?.toString().trim().length > 0);

        const hasActivity = stepHistory.length > 1 || hasInput;

        if (!hasActivity) {
            return;
        }

        // Si activité réelle, on procède au reset
        logAction(currentStep, 'INACTIVITY_TIMEOUT', { lastStep: currentStep });

        //alert("Inactivity detected!");

        handleReset();
    }, [currentStep, formData, stepHistory, logAction]);

    // Active le moniteur d'inactivité seulement sur cette page
    useInactivityMonitor(handleInactivityTimeout);

    const renderStepContent = () => {
        // On regroupe les props pour éviter les répétitions
        const stepProps = {
            formData,
            updateFormData,
            navigate: handleNavigation,
            handleReset,
            logAction
        };

        switch (currentStep) {
            case 1: return <StepOne {...stepProps} />;
            case 2: return <StepTwo {...stepProps} />;
            case 3: return <StepThree {...stepProps} />;
            case 4: return <StepFour {...stepProps} />;
            case 5: return <StepFive {...stepProps} />;
            case 6: return <StepSix {...stepProps} />;
            case 7: return <StepSeven {...stepProps} />;
            case 8: return <StepEight {...stepProps} />;
            default: return <StepOne {...stepProps} />;
        }

        //<StepOne {...stepProps} /> Équivalence: <StepOne formData={formData} updateFormData={updateFormData} ... mais en une seule ligne.
    };

    return (
        <div className="d-flex flex-column min-vh-100">
            <div className="sticky-top">
                <Header logAction={logAction} currentStep={currentStep} />
            </div>

            <div className="content-scroll-wrapper flex-grow-1 d-flex justify-content-center align-items-center">
                <div
                    className="bg-secondary p-2 rounded-2 w-100 kcard"
                    style={cardStyle}
                    key={formData.sessionId} // Force le reset de l'état local (erreurs, etc.)
                >
                    <Suspense fallback={<LoadingSpinner />}>
                        {renderStepContent()}
                    </Suspense>
                </div>
            </div>

            <div className="d-flex justify-content-end align-items-center">
                <p className="fs-6 px-2 mb-2">© {new Date().getFullYear()} Hunt Refrigeration (Canada) Inc.</p>
            </div>
        </div>
    );
};

export default Elift